import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';

interface LocationData {
  id: string;
  city: string;
  region: string;
  country: string;
  latitude: number;
  longitude: number;
  population: number;
  timezone: string;
}

interface PricingData {
  service_name: string;
  base_price: number;
  price_per_hour: number;
  price_per_mile: number;
  pricing_tiers: any;
}

// Service Card Component
interface ServiceCardProps {
  icon: string;
  title: string;
  subtitle: string;
  includes: string[];
  pricing: string;
  helper: string;
}

const ServiceCard = ({ icon, title, subtitle, includes, pricing, helper }: ServiceCardProps) => {
  return (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-500 flex flex-col h-full border border-gray-100 hover:border-teal-500 hover:-translate-y-1">
      <div className="p-8 flex-1 flex flex-col">
        <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
          <span className="text-3xl">{icon}</span>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">{title}</h3>
        <p className="text-gray-600 text-base mb-6">{subtitle}</p>
        
        <div className="mb-6 flex-1">
          <p className="text-gray-900 font-semibold text-sm mb-3 uppercase tracking-wide">What's Included:</p>
          <ul className="space-y-2.5">
            {includes.map((item, index) => (
              <li key={index} className="flex items-start text-sm text-gray-700">
                <i className="ri-checkbox-circle-fill text-teal-500 mr-3 mt-0.5 flex-shrink-0 text-lg"></i>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      
      <div className="mt-auto p-8 pt-6 bg-gradient-to-br from-gray-50 to-white border-t border-gray-100">
        <div className="mb-4">
          <div className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent mb-2">{pricing}</div>
          <p className="text-xs text-gray-500">{helper}</p>
        </div>
        <a 
          href="#book" 
          className="block w-full bg-gradient-to-r from-teal-600 to-teal-500 text-white text-center py-3.5 rounded-xl font-semibold text-base hover:from-teal-700 hover:to-teal-600 transition-all duration-300 whitespace-nowrap shadow-lg hover:shadow-xl group-hover:scale-105"
        >
          Book This Service
        </a>
      </div>
    </div>
  );
};

const cityContent: Record<string, {
  title: string;
  subtitle: string;
  description: string[];
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  heroH1?: string;
  heroSubline?: string;
  cityDescription?: {
    title: string;
    body: string[];
    trustLines?: string[];
  };
  areasPostcodes?: {
    title: string;
    body: string;
  };
  localProperty?: {
    title: string;
    body: string;
  };
  whyChoose?: {
    title: string;
    items: Array<{ icon: string; title: string; desc: string }>;
  };
  faqs?: Array<{ question: string; answer: string }>;
  finalCTA?: {
    title: string;
    body: string;
    buttonText: string;
    helperText: string;
  };
}> = {
  cardiff: {
    title: 'Home Services in Cardiff',
    subtitle: 'Transparent pricing for handyman, plumbing, electrical, cleaning, waste removal, and transport services across Cardiff.',
    heroH1: 'Home Services in Cardiff – Book a Local Professional Today',
    heroSubline: 'Transparent pricing for handyman, plumbing, electrical, cleaning, waste removal, and transport services across Cardiff.',
    description: [],
    cityDescription: {
      title: 'Home Services in Cardiff – Transparent Pricing, Local Professionals',
      body: [
        'Task-Budy provides reliable handyman, plumbing, electrical, cleaning, waste removal, and transportation services across Cardiff and surrounding areas. Our services are designed for homeowners, landlords, tenants, and property managers who need dependable help without waiting for quotes or dealing with unclear pricing.',
        'We operate across all of Cardiff and up to a 5-mile radius, covering residential homes, flats, rental properties, HMOs, and commercial spaces. Whether you need a small repair, urgent maintenance, a property clean, or waste removal, our vetted local professionals deliver efficient service with clear, upfront costs.',
        'Our pricing model is simple and transparent. Handyman, plumbing, electrical, and cleaning services are charged with a minimum first hour, followed by half-hour increments, waste removal is priced by load size, and transport & moving services include a booking fee plus mileage. There are no hidden charges — you always know how pricing works before you book.'
      ],
      trustLines: [
        'Serving all of Cardiff + 5-mile radius',
        'No quotes, no surprises',
        'Verified local professionals',
        'Book online in minutes'
      ]
    },
    areasPostcodes: {
      title: 'Areas & Postcodes We Cover in Cardiff',
      body: 'Task-Budy operates across all Cardiff postcodes, including CF10, CF11, CF14, CF23, CF24, and surrounding areas within a 5-mile radius. We serve residential properties, rental homes, HMOs, and commercial spaces throughout the city. Enter your postcode during booking to confirm coverage and see accurate pricing for your location.'
    },
    localProperty: {
      title: 'Local Property Support in Cardiff',
      body: 'Cardiff has a diverse property landscape, from Victorian terraces and modern city-centre flats to suburban family homes and student accommodation. Task-Budy supports homeowners, landlords, letting agents, and property managers with reliable maintenance, repairs, cleaning, and waste removal services. Whether you manage a single rental or a portfolio of properties, our transparent pricing and vetted professionals make property upkeep straightforward and stress-free.'
    },
    whyChoose: {
      title: 'Why Choose Task-Budy in Cardiff',
      items: [
        { icon: '✔', title: 'Transparent Pricing', desc: 'Clear costs before you book — no hidden fees or surprises.' },
        { icon: '⚡', title: 'Fast & Reliable', desc: 'Same-day availability for urgent jobs across Cardiff.' },
        { icon: '🔒', title: 'Verified Professionals', desc: 'Background-checked and insured local experts.' },
        { icon: '📍', title: 'Local Coverage', desc: 'Serving all of Cardiff and up to 5 miles beyond.' },
        { icon: '🏠', title: 'Property-Focused', desc: 'Ideal for homeowners, landlords, and property managers.' },
        { icon: '💬', title: 'Simple Booking', desc: 'Book online in minutes — no quotes, no waiting.' }
      ]
    },
    faqs: [
      {
        question: 'Do you cover all areas of Cardiff?',
        answer: 'Yes. We cover all areas of Cardiff, including the city centre and surrounding neighbourhoods, as well as locations up to a 5-mile radius outside the city.'
      },
      {
        question: 'How does pricing work for services in Cardiff?',
        answer: 'Handyman, plumbing, electrical, and cleaning services are charged with a minimum first hour, followed by half-hour increments. Waste removal is priced by load size plus a £50 booking fee. Transport & moving includes a £50 booking fee plus £1.50 per mile.'
      },
      {
        question: 'Is there a minimum charge for bookings?',
        answer: 'Yes. For hourly services, there is a minimum charge for the first hour, after which work is charged in half-hour increments.'
      },
      {
        question: 'Do you offer same-day services in Cardiff?',
        answer: 'Same-day availability may be possible depending on demand and service type. Book early to secure your preferred time.'
      },
      {
        question: 'What if my job isn\'t listed under a service category?',
        answer: 'Each category includes an "Other – describe your job" option. Select it and provide details during booking.'
      },
      {
        question: 'Do you work with landlords and property managers in Cardiff?',
        answer: 'Yes. We support landlords, letting agents, and property managers with maintenance, repairs, cleaning, waste removal, and move-related services.'
      },
      {
        question: 'Are your professionals verified and insured?',
        answer: 'Professionals are verified and background-checked. Certain services may require additional certification in line with UK regulations, which will be confirmed during booking.'
      }
    ],
    finalCTA: {
      title: 'Book a Trusted Local Professional in Cardiff',
      body: 'Whether you need a small repair, urgent maintenance, professional cleaning, waste removal, or help moving items, Task-Budy makes it easy to book reliable home services in Cardiff. Choose your service, select a time, and book online in minutes with transparent pricing and verified local professionals.',
      buttonText: 'Book a Local Professional in Cardiff',
      helperText: 'Minimum first hour applies for hourly services, followed by half-hour increments.'
    },
    metaTitle: 'Home Services in Cardiff | Same-Day Local Professionals',
    metaDescription: 'Book trusted local home services in Cardiff. Handyman, plumbing, electrical, cleaning & more. Same-day service available. Verified professionals.',
    keywords: 'home services Cardiff, handyman Cardiff, plumber Cardiff, electrician Cardiff, cleaning services Cardiff'
  },
  london: {
    title: 'Home Services in London',
    subtitle: 'Trusted Same-Day Local Professionals Across London',
    description: [
      'Need a handyman, plumber, electrician, cleaner, or waste removal service in London? Taskbudy provides fast access to experienced, vetted professionals across Greater London.',
      'Describe the job, choose when you need help, and get matched with the right professional — not the nearest guess. Prices reflect London demand but are always shown before booking, with no hidden fees or surprises.',
      'With same-day availability, real-time tracking, and 24/7 support, Taskbudy is the smarter way to book home services in London.',
      'Book reliable London home services in minutes.'
    ],
    metaTitle: 'Home Services in London | Same-Day Local Professionals',
    metaDescription: 'Book trusted local home services in London. Handyman, plumbing, electrical, cleaning & more. Same-day service available. Verified professionals.',
    keywords: 'home services London, handyman London, plumber London, electrician London, cleaning services London'
  },
  birmingham: {
    title: 'Home Services in Birmingham',
    subtitle: 'Reliable Local Home Services in Birmingham',
    description: [
      'Taskbudy helps Birmingham homeowners book trusted home services quickly and confidently. From urgent plumbing and electrical work to handyman repairs, cleaning, waste removal, and transport — we connect you with local experts you can rely on.',
      'No quotes. No chasing. Just clear pricing, live tracking, and quality service done properly.',
      'Book professional home services in Birmingham today.'
    ],
    metaTitle: 'Home Services in Birmingham | Same-Day Local Professionals',
    metaDescription: 'Book trusted local home services in Birmingham. Handyman, plumbing, electrical, cleaning & more. Same-day service available. Verified professionals.',
    keywords: 'home services Birmingham, handyman Birmingham, plumber Birmingham, electrician Birmingham, cleaning services Birmingham'
  },
  manchester: {
    title: 'Home Services in Manchester',
    subtitle: 'Same-Day Handyman, Plumbing & Electrical Services in Manchester',
    description: [
      'Taskbudy makes it easy to book trusted home services in Manchester. Whether it\'s a repair, emergency, clean, or clearance, we match you with verified Manchester-based professionals — often within the hour.',
      'Pricing is transparent, professionals are vetted, and every job is backed by our quality guarantee.',
      'Get reliable help at home in Manchester — fast.'
    ],
    metaTitle: 'Home Services in Manchester | Same-Day Local Professionals',
    metaDescription: 'Book trusted local home services in Manchester. Handyman, plumbing, electrical, cleaning & more. Same-day service available. Verified professionals.',
    keywords: 'home services Manchester, handyman Manchester, plumber Manchester, electrician Manchester, cleaning services Manchester'
  },
  leeds: {
    title: 'Home Services in Leeds',
    subtitle: 'Trusted Local Home Services in Leeds',
    description: [
      'Looking for a handyman, plumber, electrician, or cleaner in Leeds? Taskbudy connects you with experienced local professionals for same-day or scheduled services.',
      'Describe the job, choose when you need it done, and we\'ll take care of the rest — with clear pricing and no hidden fees.',
      'Book trusted home services in Leeds today.'
    ],
    metaTitle: 'Home Services in Leeds | Same-Day Local Professionals',
    metaDescription: 'Book trusted local home services in Leeds. Handyman, plumbing, electrical, cleaning & more. Same-day service available. Verified professionals.',
    keywords: 'home services Leeds, handyman Leeds, plumber Leeds, electrician Leeds, cleaning services Leeds'
  },
  liverpool: {
    title: 'Home Services in Liverpool',
    subtitle: 'Reliable Same-Day Home Services in Liverpool',
    description: [
      'Taskbudy provides fast, professional home services across Liverpool. From emergency plumbing to cleaning, waste removal, and handyman work, we connect you with trusted local experts.',
      'Every provider is vetted, insured, and rated — giving you confidence every time you book.',
      'Book Liverpool home services with confidence.'
    ],
    metaTitle: 'Home Services in Liverpool | Same-Day Local Professionals',
    metaDescription: 'Book trusted local home services in Liverpool. Handyman, plumbing, electrical, cleaning & more. Same-day service available. Verified professionals.',
    keywords: 'home services Liverpool, handyman Liverpool, plumber Liverpool, electrician Liverpool, cleaning services Liverpool'
  },
  bristol: {
    title: 'Home Services in Bristol',
    subtitle: 'Professional Home Services in Bristol — Book Online',
    description: [
      'Need reliable home services in Bristol? Taskbudy makes booking easy. Describe your issue, choose ASAP or a future date, and get matched with a skilled local professional.',
      'No quotes. No stress. Just transparent pricing and quality work.',
      'Book trusted Bristol home services today.'
    ],
    metaTitle: 'Home Services in Bristol | Same-Day Local Professionals',
    metaDescription: 'Book trusted local home services in Bristol. Handyman, plumbing, electrical, cleaning & more. Same-day service available. Verified professionals.',
    keywords: 'home services Bristol, handyman Bristol, plumber Bristol, electrician Bristol, cleaning services Bristol'
  },
  sheffield: {
    title: 'Home Services in Sheffield',
    subtitle: 'Local Handyman, Plumbing & Cleaning Services in Sheffield',
    description: [
      'Taskbudy connects Sheffield residents with trusted, verified professionals for everyday repairs, emergency work, cleaning, and waste removal.',
      'With same-day options and clear pricing, getting help at home has never been easier.',
      'Book Sheffield home services in minutes.'
    ],
    metaTitle: 'Home Services in Sheffield | Same-Day Local Professionals',
    metaDescription: 'Book trusted local home services in Sheffield. Handyman, plumbing, electrical, cleaning & more. Same-day service available. Verified professionals.',
    keywords: 'home services Sheffield, handyman Sheffield, plumber Sheffield, electrician Sheffield, cleaning services Sheffield'
  },
  nottingham: {
    title: 'Home Services in Nottingham',
    subtitle: 'Trusted Home Services in Nottingham',
    description: [
      'From plumbing and electrical repairs to cleaning and waste removal, Taskbudy provides reliable home services across Nottingham.',
      'We remove the hassle of quotes and delays — just honest pricing and professional service.',
      'Get trusted help at home in Nottingham today.'
    ],
    metaTitle: 'Home Services in Nottingham | Same-Day Local Professionals',
    metaDescription: 'Book trusted local home services in Nottingham. Handyman, plumbing, electrical, cleaning & more. Same-day service available. Verified professionals.',
    keywords: 'home services Nottingham, handyman Nottingham, plumber Nottingham, electrician Nottingham, cleaning services Nottingham'
  },
  leicester: {
    title: 'Home Services in Leicester',
    subtitle: 'Same-Day Local Home Services in Leicester',
    description: [
      'Taskbudy helps Leicester homeowners book verified local professionals for handyman work, plumbing, electrical services, cleaning, waste removal, and transport.',
      'With transparent pricing and quality-checked providers, you can book with confidence every time.',
      'Book Leicester home services quickly and easily.'
    ],
    metaTitle: 'Home Services in Leicester | Same-Day Local Professionals',
    metaDescription: 'Book trusted local home services in Leicester. Handyman, plumbing, electrical, cleaning & more. Same-day service available. Verified professionals.',
    keywords: 'home services Leicester, handyman Leicester, plumber Leicester, electrician Leicester, cleaning services Leicester'
  }
};

export default function LocationPage() {
  const { city } = useParams<{ city: string }>();
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [pricing, setPricing] = useState<PricingData[]>([]);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const cityKey = city?.toLowerCase() || '';
  const content = cityContent[cityKey];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!content || !city) return;

    const cityName = content.title.replace('Home Services in ', '');
    
    // Set page title
    document.title = content.metaTitle;

    // Remove existing meta tags
    const existingMeta = document.querySelectorAll('meta[data-city-page]');
    existingMeta.forEach(tag => tag.remove());

    // Remove existing schema scripts
    const existingSchemas = document.querySelectorAll('script[data-city-schema]');
    existingSchemas.forEach(script => script.remove());

    // Add meta tags
    const metaTags = [
      { name: 'description', content: content.metaDescription },
      { name: 'keywords', content: content.keywords },
      { name: 'geo.region', content: 'GB' },
      { name: 'geo.placename', content: cityName },
      { property: 'og:title', content: content.metaTitle },
      { property: 'og:description', content: content.metaDescription },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `https://taskbudy.co.uk/location/${city}` },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: content.metaTitle },
      { name: 'twitter:description', content: content.metaDescription }
    ];

    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      Object.entries(tag).forEach(([key, value]) => {
        meta.setAttribute(key, value);
      });
      meta.setAttribute('data-city-page', 'true');
      document.head.appendChild(meta);
    });

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://taskbudy.co.uk/location/${city}`);

    // Add LocalBusiness Schema
    const localBusinessSchema = document.createElement('script');
    localBusinessSchema.type = 'application/ld+json';
    localBusinessSchema.setAttribute('data-city-schema', 'true');
    localBusinessSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `Taskbudy - Home Services in ${cityName}`,
      "description": content.description[0],
      "url": `https://taskbudy.co.uk/location/${city}`,
      "telephone": "+44-800-TASKBUDY",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": cityName,
        "addressCountry": "GB"
      },
      "openingHours": "Mo-Su 00:00-23:59",
      "priceRange": "££-£££",
      "areaServed": {
        "@type": "City",
        "name": cityName
      }
    });
    document.head.appendChild(localBusinessSchema);

    // Add FAQ Schema
    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.setAttribute('data-city-schema', 'true');
    faqSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": `How does Taskbudy work in ${cityName}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Describe the job, choose ASAP or a later date, and Taskbudy matches you with a verified local professional in ${cityName}. You'll see transparent pricing before booking and can track the job from acceptance to arrival.`
          }
        },
        {
          "@type": "Question",
          "name": `Can I book same-day home services in ${cityName}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Yes. Same-day and emergency bookings are available in ${cityName} depending on local availability. Many jobs can be matched quickly, often within the hour.`
          }
        },
        {
          "@type": "Question",
          "name": `What services are available in ${cityName}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Taskbudy offers handyman services, plumbing, electrical work, cleaning, waste removal, and transportation services in ${cityName}. Availability may vary by area and time.`
          }
        },
        {
          "@type": "Question",
          "name": `How much do services cost in ${cityName}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Pricing in ${cityName} depends on the service type, timing, and job complexity. Taskbudy shows the final price before you confirm your booking, so there are no hidden fees or surprises. View the latest prices for ${cityName} at https://taskbudy.co.uk/location/${city}`
          }
        },
        {
          "@type": "Question",
          "name": `Are Taskbudy professionals verified and insured in ${cityName}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Taskbudy professionals are vetted and verified, and providers are insured for peace of mind. Providers are also rated after each job to help maintain quality standards."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need to get quotes before booking?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. Taskbudy is designed to remove the hassle of quotes and bidding. You see transparent pricing upfront and can book quickly without chasing multiple tradespeople."
          }
        },
        {
          "@type": "Question",
          "name": `Can I schedule a booking for a later date in ${cityName}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Yes. You can book ASAP or choose a later date and time that suits you in ${cityName}, subject to provider availability.`
          }
        },
        {
          "@type": "Question",
          "name": `Which areas around ${cityName} do you cover?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Taskbudy covers ${cityName} and surrounding areas. Enter your postcode during booking to confirm availability and see local pricing.`
          }
        }
      ]
    });
    document.head.appendChild(faqSchema);

    // Add Breadcrumb Schema
    const breadcrumbSchema = document.createElement('script');
    breadcrumbSchema.type = 'application/ld+json';
    breadcrumbSchema.setAttribute('data-city-schema', 'true');
    breadcrumbSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://taskbudy.co.uk"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Locations",
          "item": "https://taskbudy.co.uk/locations"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": cityName,
          "item": `https://taskbudy.co.uk/location/${city}`
        }
      ]
    });
    document.head.appendChild(breadcrumbSchema);

    // Add Aggregate Rating Schema
    const ratingSchema = document.createElement('script');
    ratingSchema.type = 'application/ld+json';
    ratingSchema.setAttribute('data-city-schema', 'true');
    ratingSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `Taskbudy - ${cityName}`,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "1247",
        "bestRating": "5",
        "worstRating": "1"
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": cityName,
        "addressCountry": "GB"
      }
    });
    document.head.appendChild(ratingSchema);

    return () => {
      const metaToRemove = document.querySelectorAll('meta[data-city-page]');
      metaToRemove.forEach(tag => tag.remove());
      const schemasToRemove = document.querySelectorAll('script[data-city-schema]');
      schemasToRemove.forEach(script => script.remove());
    };
  }, [city, content]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: locationData, error: locationError } = await supabase
          .from('locations')
          .select('*')
          .eq('slug', city)
          .single();

        if (locationError) throw locationError;
        setLocationData(locationData);

        if (locationData) {
          const { data: pricingData, error: pricingError } = await supabase
            .from('location_pricing')
            .select('*')
            .eq('location_slug', locationData.slug);

          if (pricingError) throw pricingError;
          setPricing(pricingData || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setLocationData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [city]);

  if (!content) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Location Not Found</h1>
          <p className="text-gray-600 mb-8">We don't currently serve this location.</p>
          <Link to="/locations" className="text-teal-600 hover:text-teal-700 font-medium">
            View All Locations
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const formatPrice = (pricing: PricingData) => {
    if (pricing.service_name === 'Transportation') {
      return `£${pricing.base_price} + £${pricing.price_per_mile}/mile`;
    } else if (pricing.service_name === 'Waste Removal') {
      const tiers = pricing.pricing_tiers;
      return `£${tiers.small} - £${tiers.full}`;
    } else {
      return `£${pricing.price_per_hour}/hour`;
    }
  };

  const cityName = content.title.replace('Home Services in ', '');
  const isCardiff = cityKey === 'cardiff';

  // Cardiff Services Data
  const cardiffServices = [
    {
      icon: '🔧',
      title: 'Handyman Services',
      subtitle: 'General repairs, installations & small jobs',
      includes: [
        'Furniture & flat-pack assembly',
        'TV, shelves & wall mounting',
        'Door repairs & handles',
        'Silicone sealing & minor plaster',
        'General home & odd jobs'
      ],
      pricing: '£45 per hour',
      helper: 'Other handyman tasks can be described during booking.'
    },
    {
      icon: '🚿',
      title: 'Plumbing Services',
      subtitle: 'Repairs, installations & plumbing maintenance',
      includes: [
        'Leaking taps & pipe repairs',
        'Blocked sinks & drains',
        'Toilet & shower repairs',
        'Radiator fixes & bleeding',
        'Appliance installations'
      ],
      pricing: '£80 per hour',
      helper: 'For unlisted plumbing issues, select "Other".'
    },
    {
      icon: '💡',
      title: 'Electrical Services',
      subtitle: 'Minor electrical work & safety checks',
      includes: [
        'Light & socket replacement',
        'New socket installation',
        'Fault finding & tripping electrics',
        'Smoke alarms & extractor fans',
        'EICR & safety checks'
      ],
      pricing: '£75 per hour',
      helper: 'Certification provided where required.'
    },
    {
      icon: '🧼',
      title: 'Cleaning Services',
      subtitle: 'Domestic & property cleaning services',
      includes: [
        'Regular & deep cleaning',
        'End-of-tenancy cleans',
        'Kitchen & bathroom deep cleans',
        'After-builders cleaning',
        'Light declutter & tidy-up'
      ],
      pricing: '£30 per hour',
      helper: 'Larger jobs may require additional time.'
    },
    {
      icon: '🗑',
      title: 'Waste Removal',
      subtitle: 'Household, garden & renovation waste',
      includes: [
        'Furniture & appliance removal',
        'Garden & DIY waste',
        'Garage, loft & office clearances',
        'Single items to full loads'
      ],
      pricing: '£50 booking fee + load size',
      helper: 'Describe your waste during booking.'
    },
    {
      icon: '🚚',
      title: 'Transportation & Moving',
      subtitle: 'Item transport & small moves',
      includes: [
        'Man & van services',
        'Furniture & marketplace delivery',
        'Student & small home moves',
        'Storage pickup/drop-off',
        'Heavy item transport'
      ],
      pricing: '£50 booking fee + £1.50 per mile',
      helper: 'Provide pickup & drop-off details when booking.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/dc77c2be5a78759d16f1acf02198b697.png" 
                alt="TaskBudy" 
                className="h-8 md:h-10 lg:h-12"
              />
            </Link>
            <div className="flex items-center space-x-6">
              <Link to="/locations" className={`font-medium transition-colors ${
                scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>
                All Locations
              </Link>
              <Link to="/services" className={`font-medium transition-colors ${
                scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'
              }`}>
                Services
              </Link>
              <Link 
                to="/service-request" 
                className="bg-gradient-to-r from-teal-600 to-teal-500 text-white px-6 py-2.5 rounded-xl font-medium hover:from-teal-700 hover:to-teal-600 transition-all duration-300 whitespace-nowrap shadow-lg hover:shadow-xl hover:scale-105"
              >
                Book Now
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-500 hover:text-teal-600 transition-colors">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/locations" className="text-gray-500 hover:text-teal-600 transition-colors">Locations</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-medium">{cityName}</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pb-32 overflow-hidden" id="book">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE1YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00em0wIDI0YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00ek0xMiAxNmMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHptMCAyNGMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
          <div className="text-center">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-map-pin-line mr-2"></i>
              Now serving {cityName}
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {content.heroH1 || content.title}
            </h1>
            <p className="text-xl md:text-2xl text-teal-50 mb-10 max-w-3xl mx-auto leading-relaxed">
              {content.heroSubline || content.subtitle}
            </p>
            <Link 
              to="/service-request" 
              className="inline-flex items-center bg-white text-teal-700 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-teal-50 transition-all duration-300 whitespace-nowrap shadow-2xl hover:shadow-3xl hover:scale-105"
            >
              Book a Service Now
              <i className="ri-arrow-right-line ml-3 text-xl"></i>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        
        {/* Cardiff City Description Section */}
        {isCardiff && content.cityDescription && (
          <section className="mb-24">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {content.cityDescription.title}
              </h2>
            </div>
            <div className="bg-white rounded-3xl shadow-xl p-10 md:p-12 border border-gray-100">
              <div className="space-y-6">
                {content.cityDescription.body.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 text-lg leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
              {content.cityDescription.trustLines && (
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {content.cityDescription.trustLines.map((line, index) => (
                    <div key={index} className="flex items-center bg-gradient-to-br from-teal-50 to-teal-100/50 p-4 rounded-xl">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 text-xl flex-shrink-0"></i>
                      <span className="text-gray-900 font-medium text-sm">{line}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Cardiff Services & Pricing Cards */}
        {isCardiff && (
          <section className="mb-24">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Services & Pricing
              </h2>
              <p className="text-xl text-gray-600">
                Transparent rates for all your home service needs
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cardiffServices.map((service, index) => (
                <ServiceCard key={index} {...service} />
              ))}
            </div>
          </section>
        )}

        {/* Cardiff Areas & Postcodes Section */}
        {isCardiff && content.areasPostcodes && (
          <section className="mb-24">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 md:p-12 text-white shadow-2xl">
              <div className="flex items-start mb-6">
                <div className="w-14 h-14 bg-teal-500 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-map-pin-line text-3xl"></i>
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    {content.areasPostcodes.title}
                  </h2>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    {content.areasPostcodes.body}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Cardiff Local Property Section */}
        {isCardiff && content.localProperty && (
          <section className="mb-24">
            <div className="bg-white rounded-3xl shadow-xl p-10 md:p-12 border border-gray-100">
              <div className="flex items-start">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-home-smile-line text-3xl text-white"></i>
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {content.localProperty.title}
                  </h2>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    {content.localProperty.body}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Cardiff Why Choose Section */}
        {isCardiff && content.whyChoose && (
          <section className="mb-24">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {content.whyChoose.title}
              </h2>
              <p className="text-xl text-gray-600">
                The smarter way to book home services
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {content.whyChoose.items.map((item, index) => (
                <div key={index} className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-teal-500 hover:-translate-y-1">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <span className="text-3xl">{item.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{item.title}</h3>
                  <p className="text-gray-600 text-center leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Cardiff FAQs Section */}
        {isCardiff && content.faqs && (
          <section className="mb-24">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about our Cardiff services
              </p>
            </div>
            <div className="space-y-4 max-w-4xl mx-auto">
              {content.faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:border-teal-500 transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="flex items-center justify-between w-full p-6 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <h3 className="text-lg font-bold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    <i className={`ri-arrow-down-s-line text-2xl text-teal-600 flex-shrink-0 transition-transform duration-300 ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}></i>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${
                    openFaqIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}>
                    <div className="px-6 pb-6 pt-2">
                      <p className="text-gray-700 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Cardiff Final CTA Section */}
        {isCardiff && content.finalCTA && (
          <section className="mb-24">
            <div className="relative overflow-hidden bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-3xl p-12 md:p-16 text-center shadow-2xl">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE1YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00em0wIDI0YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00ek0xMiAxNmMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHptMCAyNGMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
              <div className="relative">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  {content.finalCTA.title}
                </h2>
                <p className="text-xl text-teal-50 leading-relaxed mb-10 max-w-3xl mx-auto">
                  {content.finalCTA.body}
                </p>
                <a 
                  href="#book" 
                  className="inline-flex items-center bg-white text-teal-700 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-teal-50 transition-all duration-300 whitespace-nowrap shadow-2xl hover:scale-105 mb-4"
                >
                  {content.finalCTA.buttonText}
                  <i className="ri-arrow-right-line ml-3 text-xl"></i>
                </a>
                <p className="text-sm text-teal-100 mt-4">
                  {content.finalCTA.helperText}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Description Section - For non-Cardiff cities */}
        {!isCardiff && content.description && content.description.length > 0 && (
          <article className="mb-16">
            <div className="prose prose-lg max-w-none">
              {content.description.map((paragraph, index) => (
                <p key={index} className="text-gray-700 leading-relaxed mb-6 text-lg">
                  {paragraph}
                </p>
              ))}
            </div>
          </article>
        )}

        {/* Pricing Section */}
        {pricing.length > 0 && (
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Service Pricing in <strong>{cityName}</strong>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pricing.map((service) => (
                <div key={service.service_name} className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-teal-500 transition-colors">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {service.service_name}
                  </h3>
                  <p className="text-3xl font-bold text-teal-600">
                    {formatPrice(service)}
                  </p>
                  {service.service_name === 'Waste Removal' && service.pricing_tiers && (
                    <div className="mt-4 space-y-2 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span>Small (25%)</span>
                        <span className="font-semibold">£{service.pricing_tiers.small}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Half (50%)</span>
                        <span className="font-semibold">£{service.pricing_tiers.half}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>¾ (75%)</span>
                        <span className="font-semibold">£{service.pricing_tiers.three_quarter}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Full (100%)</span>
                        <span className="font-semibold">£{service.pricing_tiers.full}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Why Choose Section */}
        <section className="bg-gray-50 rounded-2xl p-12 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Why Choose <strong>Taskbudy</strong> in {cityName}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '✔', title: 'Verified Professionals', desc: 'ID-verified, background-checked & fully insured.' },
              { icon: '⚡', title: 'Same-Day Service', desc: 'Urgent help available — often within the hour.' },
              { icon: '💷', title: 'Transparent Pricing', desc: 'Upfront prices. No hidden fees. Pay only for work done.' },
              { icon: '🕘', title: '24/7 Support', desc: 'Help available whenever you need it.' },
              { icon: '⭐', title: 'Quality Guaranteed', desc: 'Not satisfied? We\'ll fix it — or refund you.' },
              { icon: '📍', title: 'Local Experts', desc: 'Trusted professionals who know your area.' }
            ].map((feature) => (
              <div key={feature.title} className="text-center">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                question: 'How does Taskbudy work?',
                answer: 'Simply describe the problem, choose whether you need help ASAP or at a later date, and we\'ll match you with a trusted local professional. You\'ll see the price before booking and can track the job from acceptance to arrival.',
                color: 'bg-teal-500'
              },
              {
                question: 'What services can I book?',
                answer: 'With Taskbudy, you can book handyman services, plumbing, electrical work, cleaning, rubbish & waste removal, and transportation & deliveries. Availability may vary by area and time.',
                color: 'bg-orange-500'
              },
              {
                question: 'Can I get same-day or emergency service?',
                answer: 'Yes. Same-day and emergency bookings are available depending on local availability. Many customers are matched with a professional within the hour.',
                color: 'bg-teal-500'
              },
              {
                question: 'How much will it cost?',
                answer: 'Pricing depends on the service, location, and job type. You\'ll always see clear pricing before confirming your booking — no hidden fees or surprise charges.',
                color: 'bg-orange-500'
              },
              {
                question: 'Do I need to get quotes?',
                answer: 'No. Taskbudy removes the hassle of quotes and bidding. Job details and pricing are shown upfront so you can book quickly with confidence.',
                color: 'bg-teal-500'
              },
              {
                question: 'Are Taskbudy professionals verified?',
                answer: 'Yes. All professionals are ID-verified, background-checked, and insured. Providers are also rated after every job to maintain quality standards.',
                color: 'bg-orange-500'
              },
              {
                question: 'Can I choose a later date instead of ASAP?',
                answer: 'Yes. You can book immediately or schedule a service for a date and time that suits you, subject to availability.',
                color: 'bg-teal-500'
              },
              {
                question: 'What areas do you cover?',
                answer: 'Taskbudy operates across multiple UK cities. Enter your postcode during booking to confirm coverage and see services available in your area.',
                color: 'bg-orange-500'
              },
              {
                question: 'What if I\'m not satisfied with the service?',
                answer: 'We stand behind our work. If you\'re not satisfied, our support team will help resolve the issue in line with our quality guarantee.',
                color: 'bg-teal-500'
              },
              {
                question: 'Is support available if I need help?',
                answer: 'Yes. Our customer support team is available to assist you with bookings, questions, or any issues before, during, or after your service.',
                color: 'bg-orange-500'
              }
            ].map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-teal-500 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 ${faq.color} rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <i className="ri-question-line text-white text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Book a Service in {cityName}?
          </h2>
          <p className="text-xl text-teal-50 mb-8">
            Get matched with verified local professionals in minutes
          </p>
          <Link 
            to="/service-request" 
            className="inline-flex items-center bg-white text-teal-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-50 transition-colors whitespace-nowrap"
          >
            Book Your Service Now
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </section>

        {/* Internal Link Block - Compare Platforms */}
        <section className="mt-16 bg-gray-50 rounded-2xl p-8 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Compare Taskbudy with Traditional Platforms
          </h2>
          <p className="text-gray-700 mb-6 leading-relaxed">
            Some people compare trade directories and quote-based platforms before booking. See how Taskbudy compares to platforms like Checkatrade, MyJobQuote, Rated People, MyBuilder and Bark — for both customers and service providers.
          </p>
          <Link
            to="/service-platform-alternatives"
            className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors"
          >
            Compare platforms
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-lg mb-4">Taskbudy</h4>
              <p className="text-gray-400 text-sm">
                Connecting you with trusted local professionals for all your home service needs.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/services" className="hover:text-white">All Services</Link></li>
                <li><Link to="/locations" className="hover:text-white">Locations</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Get Started</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/service-request" className="hover:text-white">Book a Service</Link></li>
                <li><Link to="/provider-signup" className="hover:text-white">Become a Provider</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 Taskbudy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
